<script lang="ts" module>
  // Status pill — the design-system status primitive
  // (wiki/feature-design-system.md §Component primitives, AC4). Surfaces the
  // AI-Review lifecycle states (Reviewed / Pending / Failed) and live states
  // (Recording). Carries an `aria-label` so the state is announced
  // independently of its colour (a11y). Status tone is semantic, NOT the
  // practice accent.
  //
  // Forwards arbitrary attributes (`data-testid`, `title`, `aria-*`, …) onto the
  // root via `...rest`, so a host never has to wrap the pill in a `<span>` just
  // to attach a test hook or a tooltip.
  export type PillStatus = 'reviewed' | 'pending' | 'failed' | 'recording' | 'neutral';
</script>

<script lang="ts">
  import type { Snippet } from 'svelte';
  import type { HTMLAttributes } from 'svelte/elements';
  import { t } from '$lib/i18n';
  import { UiKeys } from '@voicedojo/backend-types';

  interface Props extends HTMLAttributes<HTMLSpanElement> {
    /** Lifecycle/live status — drives colour + the default aria-label. */
    status: PillStatus;
    /**
     * Accessible label. Defaults to the status word; override when the visible
     * text differs from what should be announced.
     */
    ariaLabel?: string;
    /** Render a leading status dot (default true). */
    dot?: boolean;
    class?: string;
    /** Visible label content. */
    children: Snippet;
  }

  let {
    status,
    ariaLabel,
    dot = true,
    class: klass = '',
    children,
    ...rest
  }: Props = $props();

  const defaultLabelKeys: Record<PillStatus, string> = {
    reviewed: UiKeys.StatusPill.Reviewed,
    pending: UiKeys.StatusPill.PendingReview,
    failed: UiKeys.StatusPill.ReviewFailed,
    recording: UiKeys.StatusPill.Recording,
    neutral: UiKeys.StatusPill.Status,
  };
</script>

<span
  {...rest}
  class="vd-pill vd-pill--{status} {klass}"
  role="status"
  aria-label={ariaLabel ?? t(defaultLabelKeys[status])}
>
  {#if dot}<span class="vd-pill__dot" aria-hidden="true"></span>{/if}
  <span class="vd-pill__text">{@render children()}</span>
</span>

<style lang="scss">
  .vd-pill {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-family: var(--font-ui);
    font-size: var(--text-label);
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    line-height: 1;
  }

  .vd-pill__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
    flex: none;
  }

  // Semantic tones — theme-independent, NOT the practice accent.
  .vd-pill--reviewed {
    color: var(--color-success-text);
  }
  .vd-pill--pending {
    color: var(--ink-mute);
  }
  .vd-pill--failed {
    color: var(--color-error-text);
  }
  .vd-pill--recording {
    color: var(--color-recording-text);
  }
  .vd-pill--neutral {
    color: var(--ink-mute);
  }

  // The recording pip pulses to read as "live now"; honour reduced motion.
  .vd-pill--recording .vd-pill__dot {
    animation: vd-pill-blink 1.2s infinite;
  }
  @keyframes vd-pill-blink {
    50% {
      opacity: 0.35;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .vd-pill--recording .vd-pill__dot {
      animation: none;
    }
  }
</style>
